import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RoleProvider, useRole } from "@/context/RoleContext";
import Shell from "@/components/Shell";
import AppGate from "@/components/AppGate";
import SignIn from "@/pages/SignIn";
import Marketplace from "@/pages/Marketplace";
import DesignDetail from "@/pages/DesignDetail";
import Visualizer from "@/pages/Visualizer";
import Parcels from "@/pages/Parcels";
import ParcelDetail from "@/pages/ParcelDetail";
import ArchitectsList from "@/pages/ArchitectsList";
import ArchitectProfile from "@/pages/ArchitectProfile";
import Registry from "@/pages/Registry";
import Placeholder from "@/pages/Placeholder";
import Resources from "@/pages/Resources";
import RegisterArchitect from "@/pages/RegisterArchitect";
import ArchitectApplication from "@/pages/ArchitectApplication";
import AdminApplications from "@/pages/AdminApplications";
import AdminApplicationDetail from "@/pages/AdminApplicationDetail";
import ArchitectDashboard from "@/pages/ArchitectDashboard";
import InquiryInbox from "@/pages/InquiryInbox";
import ConsultationWorkspace from "@/pages/ConsultationWorkspace";
import ProfileEditor from "@/pages/ProfileEditor";
import ArchitectDesigns from "@/pages/ArchitectDesigns";
import DesignIntake from "@/pages/DesignIntake";
import ArchitectProjects from "@/pages/ArchitectProjects";
import ArchitectProjectDetail from "@/pages/ArchitectProjectDetail";
import CAWorkspace from "@/pages/CAWorkspace";
import OwnerDashboard from "@/pages/OwnerDashboard";
import SavedDesigns from "@/pages/SavedDesigns";
import RequestConsultation from "@/pages/RequestConsultation";
import OwnerConsultation from "@/pages/OwnerConsultation";
import OwnerMessages from "@/pages/OwnerMessages";
import OwnerProjects from "@/pages/OwnerProjects";
import OwnerProjectDetail from "@/pages/OwnerProjectDetail";
import ProfilePage from "@/pages/ProfilePage";
import PaymentsPage from "@/pages/PaymentsPage";
import ReceiptPage from "@/pages/ReceiptPage";
import CityDashboard from "@/pages/CityDashboard";
import CityReviewQueue from "@/pages/CityReviewQueue";
import CityReviewDetail from "@/pages/CityReviewDetail";
import CityHeatmap from "@/pages/CityHeatmap";
import CityCases from "@/pages/CityCases";
import CityCaseDetail from "@/pages/CityCaseDetail";
import CityProjectReadOnly from "@/pages/CityProjectReadOnly";
import CityAudit from "@/pages/CityAudit";
import CityReports from "@/pages/CityReports";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AdminArchitectsDirectory from "@/pages/AdminArchitectsDirectory";
import AdminArchitectDetail from "@/pages/AdminArchitectDetail";
import AdminArchitectEdit from "@/pages/AdminArchitectEdit";
import AdminCodeChanges from "@/pages/AdminCodeChanges";
import AdminCodeChangeDetail from "@/pages/AdminCodeChangeDetail";
import AdminReports from "@/pages/AdminReports";
import AdminSettings from "@/pages/AdminSettings";
import AdminAudit from "@/pages/AdminAudit";
import AdminEmailLog from "@/pages/AdminEmailLog";
import AdminEmailDetail from "@/pages/AdminEmailDetail";
import Notifications from "@/pages/Notifications";
import Landing from "@/pages/Landing";

function RequireRole({ allow, children }) {
  const { role } = useRole();
  if (!allow.includes(role)) {
    return <Navigate to="/sign-in" replace />;
  }
  return children;
}

/**
 * Catch-all redirect. A signed-in user who lands on an unknown path (e.g.,
 * a stale `wn.last-route.<role>` pointing to a route that's been removed)
 * should bounce to *their* role home, not the public landing. Otherwise
 * sign-in appears to drop you onto the marketing site.
 */
function NoMatch() {
  const { role, isAnonymous } = useRole();
  if (isAnonymous || !role) return <Navigate to="/sign-in" replace />;
  const HOME = {
    owner_builder: "/owner",
    architect: "/architect",
    city_staff: "/city",
    platform_admin: "/admin",
  };
  return <Navigate to={HOME[role] || "/"} replace />;
}

/**
 * Architect workspace: unapproved architects can only see the Application
 * status page. Approved architects see the actual workspace placeholder.
 */
function ArchitectArea({ children }) {
  const { role, user } = useRole();
  if (role !== "architect") return <Navigate to="/sign-in" replace />;
  if (user?.account_status !== "active") {
    return <Navigate to="/architect/application" replace />;
  }
  return children;
}

function ArchitectApplicationRoute() {
  const { role } = useRole();
  if (role !== "architect") return <Navigate to="/sign-in" replace />;
  return <ArchitectApplication />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/register/architect" element={<RegisterArchitect />} />

      <Route element={<Shell />}>
        <Route index element={<Landing />} />

        {/* Public, anyone can browse */}
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/:designId" element={<DesignDetail />} />
        <Route path="/marketplace/:designId/visualizer" element={<Visualizer />} />
        <Route path="/registry" element={<Registry />} />
        <Route path="/parcels"     element={<Parcels />} />
        <Route path="/parcels/:parcelId" element={<ParcelDetail />} />
        <Route path="/architects"  element={<ArchitectsList />} />
        <Route path="/architects/:architectId" element={<ArchitectProfile />} />
        <Route path="/resources"   element={<Resources />} />

        {/* Notifications — available to any signed-in user */}
        <Route path="/notifications" element={<Notifications />} />

        {/* Role areas (require corresponding sign-in) */}
        <Route
          path="/owner"
          element={
            <RequireRole allow={["owner_builder"]}>
              <OwnerDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/owner/saved"
          element={
            <RequireRole allow={["owner_builder"]}>
              <SavedDesigns />
            </RequireRole>
          }
        />
        <Route
          path="/owner/consultations/new"
          element={
            <RequireRole allow={["owner_builder"]}>
              <RequestConsultation />
            </RequireRole>
          }
        />
        <Route
          path="/owner/consultations/:consultId"
          element={
            <RequireRole allow={["owner_builder"]}>
              <OwnerConsultation />
            </RequireRole>
          }
        />
        <Route
          path="/owner/messages"
          element={
            <RequireRole allow={["owner_builder"]}>
              <OwnerMessages />
            </RequireRole>
          }
        />
        <Route
          path="/owner/projects"
          element={
            <RequireRole allow={["owner_builder"]}>
              <OwnerProjects />
            </RequireRole>
          }
        />
        <Route
          path="/owner/projects/:projectId"
          element={
            <RequireRole allow={["owner_builder"]}>
              <OwnerProjectDetail />
            </RequireRole>
          }
        />
        <Route
          path="/owner/payments"
          element={
            <RequireRole allow={["owner_builder"]}>
              <PaymentsPage apiPrefix="/owner-portal" role="owner_builder" />
            </RequireRole>
          }
        />
        <Route
          path="/owner/payments/:transactionRef"
          element={
            <RequireRole allow={["owner_builder"]}>
              <ReceiptPage apiPrefix="/owner-portal" backTo="/owner/payments" role="owner_builder" />
            </RequireRole>
          }
        />
        <Route
          path="/owner/profile"
          element={
            <RequireRole allow={["owner_builder"]}>
              <ProfilePage role="owner_builder" />
            </RequireRole>
          }
        />
        <Route
          path="/owner/notifications"
          element={
            <RequireRole allow={["owner_builder"]}>
              <Notifications />
            </RequireRole>
          }
        />

        {/* Architect — split: application page vs. workspace.
            Unapproved architects can ONLY hit /architect/application. */}
        <Route path="/architect/application" element={<ArchitectApplicationRoute />} />
        <Route
          path="/architect"
          element={
            <ArchitectArea>
              <ArchitectDashboard />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/inquiries"
          element={
            <ArchitectArea>
              <InquiryInbox />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/inquiries/:consultId"
          element={
            <ArchitectArea>
              <ConsultationWorkspace />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/designs"
          element={
            <ArchitectArea>
              <ArchitectDesigns />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/designs/new"
          element={
            <ArchitectArea>
              <DesignIntake />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/projects"
          element={
            <ArchitectArea>
              <ArchitectProjects />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/projects/:projectId"
          element={
            <ArchitectArea>
              <ArchitectProjectDetail />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/projects/:projectId/construction"
          element={
            <ArchitectArea>
              <CAWorkspace />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/profile"
          element={
            <ArchitectArea>
              <ProfileEditor />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/account"
          element={
            <ArchitectArea>
              <ProfilePage role="architect" />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/payments"
          element={
            <ArchitectArea>
              <PaymentsPage apiPrefix="/architect-portal" role="architect" />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/payments/:transactionRef"
          element={
            <ArchitectArea>
              <ReceiptPage apiPrefix="/architect-portal" backTo="/architect/payments" role="architect" />
            </ArchitectArea>
          }
        />
        <Route
          path="/architect/notifications"
          element={
            <ArchitectArea>
              <Notifications />
            </ArchitectArea>
          }
        />

        <Route
          path="/city"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/city/reviews"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityReviewQueue />
            </RequireRole>
          }
        />
        <Route
          path="/city/reviews/:designId"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityReviewDetail />
            </RequireRole>
          }
        />
        <Route
          path="/city/heatmap"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityHeatmap />
            </RequireRole>
          }
        />
        <Route
          path="/city/cases"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityCases />
            </RequireRole>
          }
        />
        <Route
          path="/city/cases/:caseId"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityCaseDetail />
            </RequireRole>
          }
        />
        <Route
          path="/city/projects/:projectId"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityProjectReadOnly />
            </RequireRole>
          }
        />
        <Route
          path="/city/audit"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityAudit />
            </RequireRole>
          }
        />
        <Route
          path="/city/reports"
          element={
            <RequireRole allow={["city_staff"]}>
              <CityReports />
            </RequireRole>
          }
        />
        <Route
          path="/city/notifications"
          element={
            <RequireRole allow={["city_staff"]}>
              <Notifications />
            </RequireRole>
          }
        />
        <Route
          path="/city/account"
          element={
            <RequireRole allow={["city_staff"]}>
              <ProfilePage role="city_staff" />
            </RequireRole>
          }
        />
        <Route
          path="/code-changes"
          element={<Navigate to="/admin/code-changes" replace />}
        />
        <Route
          path="/admin"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminDashboard />
            </RequireRole>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminUsers />
            </RequireRole>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminUserDetail />
            </RequireRole>
          }
        />
        <Route
          path="/admin/architects"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminArchitectsDirectory />
            </RequireRole>
          }
        />
        <Route
          path="/admin/architects/:architectId"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminArchitectDetail />
            </RequireRole>
          }
        />
        <Route
          path="/admin/architects/:architectId/edit"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminArchitectEdit />
            </RequireRole>
          }
        />
        <Route
          path="/admin/code-changes"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminCodeChanges />
            </RequireRole>
          }
        />
        <Route
          path="/admin/code-changes/:ccId"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminCodeChangeDetail />
            </RequireRole>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminReports />
            </RequireRole>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminSettings />
            </RequireRole>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminAudit />
            </RequireRole>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <RequireRole allow={["platform_admin"]}>
              <PaymentsPage apiPrefix="/admin-portal" role="platform_admin" />
            </RequireRole>
          }
        />
        <Route
          path="/admin/payments/:transactionRef"
          element={
            <RequireRole allow={["platform_admin"]}>
              <ReceiptPage apiPrefix="/admin-portal" backTo="/admin/payments" role="platform_admin" />
            </RequireRole>
          }
        />
        <Route
          path="/admin/notifications"
          element={
            <RequireRole allow={["platform_admin"]}>
              <Notifications />
            </RequireRole>
          }
        />
        <Route
          path="/admin/account"
          element={
            <RequireRole allow={["platform_admin"]}>
              <ProfilePage role="platform_admin" />
            </RequireRole>
          }
        />
        <Route
          path="/admin/applications"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminApplications />
            </RequireRole>
          }
        />
        <Route
          path="/admin/applications/:applicationId"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminApplicationDetail />
            </RequireRole>
          }
        />
        <Route
          path="/admin/email-log"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminEmailLog />
            </RequireRole>
          }
        />
        <Route
          path="/admin/email-log/:emailId"
          element={
            <RequireRole allow={["platform_admin"]}>
              <AdminEmailDetail />
            </RequireRole>
          }
        />

        <Route path="*" element={<NoMatch />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AppGate>
        <BrowserRouter>
          <RoleProvider>
            <AppRoutes />
          </RoleProvider>
        </BrowserRouter>
      </AppGate>
    </div>
  );
}
